import { AI_VIEW, COMMENT_VIEW } from 'components/DocRightRail/Constants'
import { REACT_APP_BLAZE } from 'env'
import { autorun, makeAutoObservable, reaction } from 'mobx'
import { ArticleView, DocRightRailSection, DocRightRailViewItem } from 'types'
import { DocStore } from './DocStore'
import { CollabItemType } from './DocStore.types'

export const LOCAL_STORAGE_KEY = 'almanac-right-rail'

export class DocRightRailStore {
  docStore: DocStore
  activeTab?: DocRightRailViewItem
  sections: DocRightRailSection[]
  isAddingProperty: boolean = false
  disposers: Array<() => void> = []

  constructor(docStore: DocStore) {
    this.docStore = docStore
    this.sections = [
      {
        key: 'todos',
        label: 'Tasks',
        collapsed: false,
        type: CollabItemType.Todo,
        items: (type: CollabItemType) => this.docStore.orderedFilteredCollabItems.filter((item) => item.type === type),
        showOverride: () => false,
      },
      {
        key: 'comments',
        label: 'Comments',
        collapsed: false,
        type: CollabItemType.Comment,
        items: (type: CollabItemType) => this.docStore.orderedFilteredCollabItems.filter((item) => item.type === type),
        showOverride: () => false,
      },
      {
        key: 'trackedChanges',
        label: 'Changes',
        collapsed: false,
        type: CollabItemType.TrackedChange,
        items: (type: CollabItemType) => this.docStore.orderedFilteredCollabItems.filter((item) => item.type === type),
        showOverride: () =>
          (this.docStore.filters.activeBaseKeys.length === 0 ||
            this.docStore.filters.activeKeys.has('trackedChanges')) &&
          this.docStore.articleView === ArticleView.Editing,
      },
    ]
    makeAutoObservable(this)
    this.hydrate()
    this.disposers = [
      autorun(() => {
        this.saveToLocalStorage()
      }),
      reaction(
        () => this.docStore.rootStore.layout.rightColumnLocked,
        (isLocked) => this.onLocked(isLocked),
      ),
    ]
  }

  get toJSON() {
    return {
      locked: this.locked,
    }
  }

  saveToLocalStorage = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.toJSON))
  }

  hydrate = () => {
    const dehydratedStr = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!dehydratedStr) {
      return
    }

    const { locked } = JSON.parse(dehydratedStr)
    this.setLocked(locked)
  }

  setHovered = (isHovered: boolean) => {
    this.docStore.rootStore.layout.setRightColumnHovered(isHovered)
  }

  setLocked(locked: boolean) {
    this.docStore.rootStore.layout.setRightColumnLocked(locked)
  }

  onLocked = (locked: boolean) => {
    if (!locked) {
      this.setActiveTab(undefined)
    }
    if (locked && !this.activeTab) {
      this.setActiveTab(REACT_APP_BLAZE ? AI_VIEW : COMMENT_VIEW)
    }
  }

  setAddingProperty = (isAddingProperty: boolean) => {
    this.isAddingProperty = isAddingProperty
  }

  setActiveTab = (tab?: DocRightRailViewItem) => {
    this.activeTab = tab
  }

  toggleActiveTab = (tab: DocRightRailViewItem) => {
    if (this.activeTab === tab) {
      this.setActiveTab(undefined)
    } else {
      this.setActiveTab(tab)
    }
  }

  toggleSection = (sectionKey: string) => {
    const section = this.sections.find((section) => section.key === sectionKey)
    if (!section) return

    section.collapsed = !section.collapsed
  }

  openSection = (sectionKey: string) => {
    this.docStore.filters.clear()
    if (this.isSectionCollapsed(sectionKey)) {
      this.toggleSection(sectionKey)
    }
  }

  isSectionCollapsed = (sectionKey: string) => {
    return Boolean(this.sections.find((section) => section.key === sectionKey)?.collapsed)
  }

  get locked() {
    return this.docStore.rootStore.layout.rightColumnLocked
  }

  get isHovered() {
    return this.docStore.rootStore.layout.rightColumnIsHovered
  }

  destroy() {
    this.disposers.forEach((disposer) => disposer())
  }
}
